import type { Role } from "@agentry/zerovia-contracts";
import {
	type Area,
	enabledScreenModules,
	SCREEN_MODULES,
	type ScreenModule,
} from "./screen-module";

export type { Area } from "./screen-module";

export type NavItem = {
	label: string;
	to: string;
	exact?: boolean;
	icon?: "workspace" | "storage";
};

export type NavGroup = { title: string; items: NavItem[] };

export type RouteSpec = { path: string; area: Area; title: string };

/** サイド上部のアイコン付き項目。タイトルは出さない。 */
export const PRIMARY_NAV_GROUP = "";

const GROUP_ORDER = [PRIMARY_NAV_GROUP, "はたらく", "確認する", "自分の設定"] as const;

export function navigationFor(modules: readonly ScreenModule[], area: Area): NavGroup[] {
	const groups = new Map<string, NavItem[]>();
	for (const module of enabledScreenModules(modules)) {
		// 空文字 "" は上部ショートカット用の正当な group（falsy 判定で落とさない）
		if (module.area !== area || module.navGroup == null) continue;
		const items = groups.get(module.navGroup) ?? [];
		items.push({
			label: module.label,
			to: module.path,
			exact: module.path === "/app" || module.path === "/admin" || module.path === "/system",
			icon: module.navIcon,
		});
		groups.set(module.navGroup, items);
	}
	const orderedTitles = [
		...GROUP_ORDER.filter((title) => groups.has(title)),
		...[...groups.keys()].filter(
			(title) => !(GROUP_ORDER as readonly string[]).includes(title),
		),
	];
	return orderedTitles.map((title) => {
		const items = groups.get(title) ?? [];
		if (title === PRIMARY_NAV_GROUP) {
			const rank = (icon?: NavItem["icon"]) =>
				icon === "workspace" ? 0 : icon === "storage" ? 1 : 2;
			return {
				title,
				items: [...items].sort((left, right) => rank(left.icon) - rank(right.icon)),
			};
		}
		return { title, items };
	});
}

export const ROUTE_REGISTRY: RouteSpec[] = enabledScreenModules(SCREEN_MODULES).map((module) => ({
	path: module.path,
	area: module.area,
	title: module.label,
}));

export const END_USER_NAV = navigationFor(SCREEN_MODULES, "end-user");
export const TENANT_ADMIN_NAV = navigationFor(SCREEN_MODULES, "tenant-admin");
export const SYSTEM_ADMIN_NAV = navigationFor(SCREEN_MODULES, "system-admin");
export const PUBLIC_NAV = navigationFor(SCREEN_MODULES, "public").flatMap((group) => group.items);

function rolesForArea(area: Area): Role[] {
	return [
		...new Set(
			enabledScreenModules(SCREEN_MODULES)
				.filter((module) => module.area === area)
				.flatMap((module) => module.roles),
		),
	];
}

export const AREA_ROLES: Record<Area, ReadonlyArray<Role>> = {
	public: rolesForArea("public"),
	"end-user": rolesForArea("end-user"),
	"tenant-admin": rolesForArea("tenant-admin"),
	"system-admin": rolesForArea("system-admin"),
};

export function canAccessRoles(roles: readonly Role[], role: Role | "anonymous"): boolean {
	if (roles.length === 0) return true;
	return role !== "anonymous" && roles.includes(role);
}

export function canAccess(area: Area, role: Role | "anonymous"): boolean {
	if (area === "public") return true;
	return canAccessRoles(AREA_ROLES[area], role);
}

export function landingFor(role: Role | "anonymous"): string {
	if (role === "anonymous") return "/login";
	// platform_viewer は /app に入れないため、入れる画面だけ例外にする。
	if (role === "platform_viewer") return "/system";
	return "/app/workspace";
}
